import {
  Pagination,
  Table,
  useTableHelpers,
  useTablePagination,
} from '@teamsnap/snap-ui';

import { currencyGivenPennies } from 'core/utils';
import {
  LoadingType,
  useOrderDetails,
  useOrderDetailsActions,
} from 'hooks/orderDetails';
import { startCase } from 'lodash';
import { DateTime } from 'luxon';
import * as React from 'react';
import OrderDetailsTableSkeleton from '../order-table-skeleton';
import { CancelInstallmentModal } from './cancel-installment-modal';
import { EditInstallmentModal } from './edit-installment-modal';
import {
  getColumns,
  getPaymentDescription,
  getPaymentMethodType,
} from './helpers';

const InstallmentsList = () => {
  const [editModalIsOpen, setEditModalIsOpen] = React.useState<boolean>(false);
  const [cancelModalIsOpen, setCancelModalIsOpen] =
    React.useState<boolean>(false);
  const [currentInstallment, setCurrentInstallment] = React.useState(undefined);

  const { order, loading, installments } = useOrderDetails();
  const { getInstallments } = useOrderDetailsActions();

  React.useEffect(() => {
    if (!installments || !installments.length) {
      getInstallments(order?.id);
    }
  }, [installments, order]);

  const parsedData = React.useMemo(() => {
    if (!installments || !installments.length) return [];
    return installments.map((installment, i) => ({
      ...installment,
      description: getPaymentDescription(i),
      amount: currencyGivenPennies(installment.amount),
      dueDate: installment.scheduledDate
        ? DateTime.fromJSDate(
            new Date(`${installment.scheduledDate} 00:00:00`)
          ).toFormat('MM/dd/yyyy')
        : '',
      paidDate: installment.paidAt
        ? DateTime.fromJSDate(new Date(installment.paidAt)).toFormat(
            'MM/dd/yyyy'
          )
        : '',
      status: startCase(installment.status),
      id: installment.id,
      transactionId: installment.transactionId,
      paymentMethodType: getPaymentMethodType(installment),
    }));
  }, [installments]);

  const { rows, headerRow } = useTableHelpers({
    data: parsedData,
    columns: { ...getColumns(handleOpenEditModal, handleOpenCancelModal) },
    initialSorting: {
      method: 'date',
      sortKey: 'paidDate',
      acsending: true,
    },
  });

  const { currentPage, onPageChange, totalCount, currentRows, pageSize } =
    useTablePagination({
      rows,
      pageSize: 10,
    });

  function handleOpenEditModal(installment) {
    setCurrentInstallment(installment);
    setEditModalIsOpen(true);
  }

  function handleOpenCancelModal(installment) {
    setCurrentInstallment(installment);
    setCancelModalIsOpen(true);
  }

  function handleCloseModal() {
    setEditModalIsOpen(false);
    setCancelModalIsOpen(false);
    setCurrentInstallment(undefined);
  }

  return (
    <div
      className="u-flex u-flexCol u-padBottomXxl"
      data-testid="installments-table--container"
    >
      {loading[LoadingType.GetInstallments] ? (
        <OrderDetailsTableSkeleton />
      ) : (
        <>
          <Table
            data-testid="installments-table--table"
            headerRow={headerRow}
            rows={currentRows}
          />
          <Pagination
            className="sui-mt-3"
            {...{ currentPage, onPageChange, totalCount, pageSize }}
          />
        </>
      )}
      <CancelInstallmentModal
        isOpen={cancelModalIsOpen}
        closeFn={handleCloseModal}
        installment={currentInstallment}
        orderId={order?.id}
      />
      {currentInstallment && (
        <EditInstallmentModal
          isOpen={editModalIsOpen}
          closeFn={handleCloseModal}
          installment={currentInstallment}
          orderId={order?.id}
        />
      )}
    </div>
  );
};

export default InstallmentsList;
